import React from 'react';
import { useParams } from 'react-router';
import { useMutation, useQuery } from '@apollo/client';
import { Card, message } from 'antd';
import sanitizeHtml from 'sanitize-html';
import PropTypes from 'prop-types';
import { useProjectFrozen } from 'hooks';
import { sortBySequence } from 'utils';
import { GET_PENDING_APPROVALS } from 'gql/pendingApprovals';
import { IS_CURRENT_APPOINTED_APPROVER } from 'gql/auth';
import { APPLICATION_TYPES } from 'constants/form';
import ApprovalFrom from './ApprovalForm';
import ApprovedDetail from './ApprovedDetail';

const ApprovalSection = ({
  approvalRoute,
  applicationType,
  mutation,
  refetchQueries,
}) => {
  const { id, projectId } = useParams();
  const projectFrozen = useProjectFrozen(projectId);
  const {
    data: isCurrentAppointedApprover,
    loading: isCurrentAppointedApproverLoading,
  } = useQuery(IS_CURRENT_APPOINTED_APPROVER, {
    variables: {
      id,
      applicationType,
    },
    // fetchPolicy: 'cache-and-network',
  });

  const [approve] = useMutation(mutation, {
    refetchQueries: [
      ...refetchQueries,
      {
        query: GET_PENDING_APPROVALS,
        variables: {
          ignoreNotAuthorised: true,
        },
        errorPolicy: 'all',
      },
      {
        query: IS_CURRENT_APPOINTED_APPROVER,
        variables: { id, applicationType },
      },
    ],
    awaitRefetchQueries: true,
    onCompleted: () => {
      message.success('Approval result submitted successfully');
    },
  });
  const steps = sortBySequence(approvalRoute?.approvalSteps);

  return (
    approvalRoute && (
      <Card title="Approval">
        {steps?.map((step) =>
          step.sequence < approvalRoute.sequence ? (
            <ApprovedDetail
              step={step}
              key={`approval-${id}-${step.sequence}`}
            />
          ) : (
            <ApprovalFrom
              isCurrentAppointedApprover={
                isCurrentAppointedApprover?.isCurrentAppointedApprover
              }
              projectFrozen={projectFrozen}
              applicationType={applicationType}
              loading={isCurrentAppointedApproverLoading}
              active={step.sequence === approvalRoute.sequence}
              step={step}
              refetchQueries={[
                ...refetchQueries,
                {
                  query: IS_CURRENT_APPOINTED_APPROVER,
                  variables: { id, applicationType },
                },
              ]}
              key={`approval-${id}-${step.sequence}`}
              onFinish={async ({ approved, comment }) => {
                await approve({
                  variables: {
                    input: {
                      id,
                      projectId,
                      sequence: step.sequence,
                      approved,
                      comment: sanitizeHtml(comment),
                    },
                  },
                });
              }}
            />
          )
        )}
      </Card>
    )
  );
};

ApprovalSection.defaultProps = {
  approvalRoute: {},
  refetchQueries: [],
  applicationType: '',
};

ApprovalSection.propTypes = {
  approvalRoute: PropTypes.object.isRequired,
  applicationType: PropTypes.oneOf(APPLICATION_TYPES).isRequired,
  mutation: PropTypes.object.isRequired,
  refetchQueries: PropTypes.array.isRequired,
};

export default ApprovalSection;
